/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, { useEffect, useMemo } from "react";
import ArgosFlexbox from "../../../../../../components/Containers/ArgosFlexbox";
import TextField from "../../../../../../components/Form/TextField/ArgosTextField";
import { useAppDispatch, useAppSelector } from "../../../../../../../redux/hooks";
import {
  fetchMergedDataSetsForAdasProgram, fetchObjectsForAdasProgram,
  selectConnectAdasProgramWizard, setConnectAdasProgramWizardData
} from "../../../../../../../redux/ducks/SensingSystemArchitectureDesign";
import { selectPrograms } from "../../../../../../../redux/ducks/AdasPrograms";
import { Autocomplete, Box } from "@mui/material";
import ADAS_PROGRAMS_CONSTANTS from "../../../../../AdasPrograms/constants";
import { useFetchToken } from "../../../../../../../api/helper";
import { ArgosPlanOfRecordIcon as PlanOfRecordIcon} from "../../../../../../components/Icons/ArgosActionIcons";
import { tableButtonGray } from "../../../../../../../constants/colors";


/** Allows the user to find and select an ADAS Program */
export const SelectProgramStep = React.memo(() => {
  const dispatch = useAppDispatch()
  const formData = useAppSelector(selectConnectAdasProgramWizard).data
  const adasPrograms = useAppSelector(selectPrograms)

  const adasProgramOptions = useMemo(() => {
    return [...adasPrograms].sort((a, b) => {
      if (a.data.programName.toLowerCase() < b.data.programName.toLowerCase()) return -1
      if (a.data.programName.toLowerCase() > b.data.programName.toLowerCase()) return 1
      return a.data.modelYear.localeCompare(b.data.modelYear)
    }).map((program) => {
      return {
        'id': program.id,
        'program': program.data.programName,
        'isPlanOfRecord': program.data.isPlanOfRecord,
        'label': ADAS_PROGRAMS_CONSTANTS.helperFunctions.getFullAdasProgramNameWithNameAndModelYear(program.data.programName, program.data.modelYear),
      }
    })
  }, [adasPrograms])

  const selectedValue = useMemo(
    () => adasProgramOptions.filter((v) => v.id === formData?.adasProgramId),
    [adasProgramOptions, formData?.adasProgramId],
  )

  /** Fetch object data once a program is selected */
  const fetchToken = useFetchToken()

  useEffect(() => {
    const fetchObjectsForAdasProgramData = async (adasProgramId: string) => {
      const tokenFunction = await fetchToken
      const idToken = await tokenFunction()
      dispatch(fetchObjectsForAdasProgram({idToken, adasProgramId}))
    }
    const fetchMergedDataSetsForAdasProgramData = async (adasProgramId: string) => {
      const tokenFunction = await fetchToken
      const idToken = await tokenFunction()
      dispatch(fetchMergedDataSetsForAdasProgram({idToken, adasProgramId}))
    }
    if (selectedValue && selectedValue.length > 0) {
      const adasProgramId = selectedValue[0]?.id
      fetchObjectsForAdasProgramData(adasProgramId)
      fetchMergedDataSetsForAdasProgramData(adasProgramId)
    }
  }, [selectedValue])

  return (
    <ArgosFlexbox vertical style={{height: '100%', width: '100%', gap: 20, padding: '10px'}}>
      <Autocomplete
        id='adas-program-select'
        value={selectedValue.length > 0 ? selectedValue[0] : null}
        autoHighlight
        style={{width: '100%'}}
        options={adasProgramOptions}
        groupBy={(option) => option.program}
        getOptionLabel={(option) => option.label}
        renderOption={(props: any, option) => {
          const { key, ...optionProps } = props
          return (
            <Box
              key={key}
              component='li'
              gap='8px'
              {...optionProps}
            >
              {option.label}
              {option.isPlanOfRecord && <span>
                <PlanOfRecordIcon style={{color: tableButtonGray}} />
              </span>}
            </Box>
          )
        }}
        renderInput={(params) => <TextField {...params} variant='standard' label='ADAS Program' />}
        onChange={(event, option) => {
          if (option) {
            dispatch(setConnectAdasProgramWizardData({...formData, adasProgramId: option.id}))
          } else {
            dispatch(setConnectAdasProgramWizardData({...formData, adasProgramId: ''}))
          }
        }}
      />
    </ArgosFlexbox>
  )
})
